import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PopUpComponent } from '../components/pop-up/pop-up.component';
import { SosAlertComponent } from '../components/sos-alert/sos-alert.component';
import { EbsAlertComponent } from '../components/ebs-alert/ebs-alert.component';
import { ShowAlertInfoComponent } from '../components/show-alert-info/show-alert-info.component';
import { ShowAlertInfoIntegrateComponent } from '../components/show-alert-info-integrate/show-alert-info-integrate.component';
import { ShowEbsalertInfoComponent } from '../components/show-ebsalert-info/show-ebsalert-info.component';
import { ShowMapInfoComponent } from '../components/show-map-info/show-map-info.component';
import { Observable, ReplaySubject } from 'rxjs';
import { Subject } from 'rxjs/Subject';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { FormModalComponent } from '../components/form-modal/form-modal.component';
import { FormQuestionnaireComponent } from '../components/form-questionnaire/form-questionnaire.component';
import { CaregiverService } from './caregiver.service';
import { NotificationService } from './notification.service';
import { PopUpHtmlComponent } from '../components/pop-up-html/pop-up-html.component';
import { PopUpRegisterComponent } from '../components/pop-up-register/pop-up-register.component';
import { PopUpReportsComponent } from '../components/pop-up-reports/pop-up-reports.component';
import {PopUpDocumentComponent} from '../components/pop-up-document/pop-up-document.component';
import {PopUpReportsUserComponent} from '../components/pop-up-reports-user/pop-up-reports-user.component';

@Injectable()
export class ModalService {

  private alarmWindow: { discr: string, modalRef: any } = null;
  private openSensorAlarmIds = new Set<string>();
  public turnOnAlerts = true;

  constructor(
    private modalService: NgbModal,
    private router: Router,
    private translate: TranslateService,
    private caregiverService: CaregiverService,
    private notificationService: NotificationService
  ) { }

  // Rejestracja nazywa każdy powód odrzucenia tak samo, żeby nie zdradzać, czy podany
  // e-mail albo IMEI już w systemie istnieje.
  openFormInvalid(eventId?: string) {
    this.open(this.translate.instant('BACKEND.FORM_INVALID'), undefined, undefined, eventId);
  }

  open(message: string, route?: string, error?: boolean, eventId?: string) {
    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.eventId = eventId;
      modalRef.componentInstance.error = error;

      if (route) {
        modalRef.result.then((result) => {
          this.router.navigateByUrl(route);
        }).catch(() => { });
      } else {
        modalRef.result.catch(() => { });
      }
    });
  }

  openOk(message: string, eventId?: string): Observable<any> {
    const response: Subject<any> = new Subject<any>();

    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.eventId = eventId;

      modalRef.result.then(
        (result) => response.next({ ok: true })
      ).catch(
        (reject) => response.next({ ok: false })
      );

    });
    return response;
  }

  openMailSend(message: string, route?: string) {
    setTimeout(() => {
      const modalRef = this.modalService.open(FormModalComponent, { centered: true, backdrop: 'static', keyboard: true });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.mail = message;

      if (route) {
        modalRef.result.then((result) => {
          this.router.navigateByUrl(route);
        }).catch(() => { });
      } else {
        modalRef.result.catch(() => { });
      }
    });
  }

  openQuestionnareSend(message: string, route?: string) {
    setTimeout(() => {
      const modalRef = this.modalService.open(FormQuestionnaireComponent, { centered: true, backdrop: 'static', keyboard: true });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.message = message;

      if (route) {
        modalRef.result.then((result) => {
          this.router.navigateByUrl(route);
        }).catch(() => { });
      } else {
        modalRef.result.catch(() => { });
      }
    });
  }

  openAccept(message: string, eventId?: string): Observable<any> {
    const response: Subject<any> = new Subject<any>();

    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 2;
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.eventId = eventId;

      modalRef.result.then(
        (result) => response.next({ ok: true })
      ).catch(
        (reject) => response.next({ ok: false })
      );

    });
    return response;
  }

  /**
   * Potwierdzenie, po którym akcja wykonuje się PRZY OTWARTYM oknie: „Tak" pokazuje na
   * sobie postęp, „Anuluj" jest w tym czasie zablokowany, a okno zamyka się dopiero po
   * zakończeniu. Zwykły openAccept() zamyka okno w chwili kliknięcia, więc żądanie leciało
   * już „w próżni" — nie było czego oznaczyć jako w toku.
   *
   * Kontrakt zwracanego strumienia:
   *   anulowano  → kończy się bez emisji (wołający nie robi nic),
   *   udało się  → emituje wynik akcji, potem kończy,
   *   błąd       → strumień błądzi, żeby wołający pokazał swój komunikat.
   */
  openAcceptAndRun<T>(message: string, action: () => Observable<T>, busyLabelKey?: string, eventId?: string): Observable<T> {
    const response: ReplaySubject<T> = new ReplaySubject<T>(1);

    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 2;
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.eventId = eventId;
      modalRef.componentInstance.confirmAction = action;
      if (busyLabelKey) {
        modalRef.componentInstance.confirmBusyLabel = busyLabelKey;
      }

      modalRef.result.then(
        result => { response.next(result); response.complete(); },
        reason => reason && reason.actionFailed ? response.error(reason.error) : response.complete()
      );
    });

    return response.asObservable();
  }

  // Wybór: jeden podopieczny vs wielu (import Excel). Emituje 'one', 'many' albo null (zamknięcie).
  // Można zamknąć bez wyboru: przyciskiem X, klawiszem Esc lub kliknięciem poza oknem.
  openChoice(message: string, eventId?: string): Observable<string> {
    const response: Subject<string> = new Subject<string>();

    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpComponent, { centered: true, backdrop: true, keyboard: true });
      modalRef.componentInstance.modalType = 3;
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.eventId = eventId;

      modalRef.result.then(
        (result) => response.next(result)
      ).catch(
        () => response.next(null)
      );
    });
    return response;
  }

  private getAlarmPriority(discr: string): number {
    switch (discr) {
      case 'alarmsos': return 3;
      case 'alarmfall': return 3;
      case 'alarmleavezone': return 1;
      default: return 0;
    }
  }

  openSosAlert(user) {
    if (this.turnOnAlerts) {
      if (this.alarmWindow) {
        if (this.getAlarmPriority(user.discr) <= this.getAlarmPriority(this.alarmWindow.discr)) {
          return;
        }
        if (this.alarmWindow.modalRef) {
          this.alarmWindow.modalRef.dismiss('replaced');
        }
      }

      const alarmWindow = { discr: user.discr, modalRef: null };
      this.alarmWindow = alarmWindow;
      setTimeout(() => {
        if (this.alarmWindow !== alarmWindow) {
          return;
        }
        const modalRef = this.modalService.open(SosAlertComponent, {
          centered: true,
          backdrop: 'static',
          keyboard: false,
          windowClass: 'sos-alert-modal-window'
        });
        modalRef.componentInstance.user = user;
        alarmWindow.modalRef = modalRef;

        modalRef.result.then((result) => {
          this.releaseAlarmWindow(alarmWindow);
          sessionStorage.removeItem('pendingAlarms');
          this.router.navigateByUrl('/users/sos-request/' + user.alarmID);
        }, (reject) => {
          this.releaseAlarmWindow(alarmWindow);
        }).catch(() => { });

      });
    }
  }

  private releaseAlarmWindow(alarmWindow: { discr: string, modalRef: any }) {
    if (this.alarmWindow === alarmWindow) {
      this.alarmWindow = null;
    }
  }

  openSensorAlert(user) {
    if (this.turnOnAlerts) {
      const alarmId = String(user.alarmID);
      if (!this.openSensorAlarmIds.has(alarmId)) {
        this.openSensorAlarmIds.add(alarmId);
        setTimeout(() => {
          const modalRef = this.modalService.open(EbsAlertComponent, {
            centered: true,
            backdrop: 'static',
            keyboard: false,
            windowClass: 'sos-alert-modal-window'
          });
          modalRef.componentInstance.user = user;

          modalRef.result.then((result) => {
            this.openAccept(
              this.translate.instant('EBS.HANDLE-EBSALARM', {}),
              'EVENTS.SHARED.SERVICES.MODAL.POPUP.EBS-HANDLE-EBSALARM'
            )
              .pipe(take(1))
              .subscribe(data => {
                this.openSensorAlarmIds.delete(alarmId);
                if (data.ok === true) {
                  sessionStorage.removeItem('pendingAlarms');
                  this.router.navigateByUrl('/users/ebs-request/' + user.alarmID);
                } else {
                  // Anuluj — jak w SOS: zatrzymaj zapętlony dźwięk (wcześniej grał dalej)
                  this.stopSirenUnlessAlarmWindowOpen();
                }
              });
          }, (reject) => {
            this.openSensorAlarmIds.delete(alarmId);
            this.stopSirenUnlessAlarmWindowOpen();
          }).catch(() => { });

        });
      }
    }
  }

  private stopSirenUnlessAlarmWindowOpen() {
    if (!this.alarmWindow && !this.openSensorAlarmIds.size) {
      this.notificationService.stopAlarm();
    }
  }

  turnOffAlert(status) {
    this.turnOnAlerts = status;
  }

  openAlertInfo(data, bandType) {
    setTimeout(() => {
      const modalRef = this.modalService.open(ShowAlertInfoComponent, { backdrop: 'static', keyboard: false });
      modalRef.componentInstance.data = data;
      modalRef.componentInstance.bandType = bandType;
    });
  }

  openAlertInfoIntegrate(data, bandType) {
    setTimeout(() => {
      const modalRef = this.modalService.open(ShowAlertInfoIntegrateComponent, {
        backdrop: true,
        keyboard: true,
        centered: true,
        windowClass: 'draggable-modal'
      });
      modalRef.componentInstance.data = data;
      modalRef.componentInstance.bandType = bandType;
    });
  }

  openEbsAlertInfo(data, bandType, firstName, lastName, avatar) {
    setTimeout(() => {
      const modalRef = this.modalService.open(ShowEbsalertInfoComponent, { backdrop: 'static', keyboard: false });
      modalRef.componentInstance.data = data;
      modalRef.componentInstance.bandType = bandType;
      modalRef.componentInstance.firstName = firstName;
      modalRef.componentInstance.lastName = lastName;
      modalRef.componentInstance.avatar = avatar;
    });
  }

  openMapInfo(data, beacon, accuracy) {
    setTimeout(() => {
      const modalRef = this.modalService.open(ShowMapInfoComponent, { centered: true, backdrop: true, keyboard: true, windowClass: 'draggable-modal' });
      modalRef.componentInstance.data = data;
      modalRef.componentInstance.beacon = beacon;
      modalRef.componentInstance.accuracy = accuracy;
    });
  }

  openStartInfo(message: string, route?: string, eventId?: string) {
    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpHtmlComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.eventId = eventId;

      if (route) {
        modalRef.result.then((result) => {
          this.router.navigateByUrl(route);
        }).catch(() => { });
      }
    });
  }

  openAcceptAwait(message: string, route?: string, eventId?: string): Observable<any> {
    const response: Subject<any> = new Subject<any>();

    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.eventId = eventId;

      modalRef.result.then(
        (result) => response.next({ ok: true })
      ).catch(
        (reject) => response.next({ ok: false })
      );

      if (route) {
        modalRef.result.then((result) => {
          this.router.navigateByUrl(route);
        }).catch(() => { });
      }

    });
    return response;
  }

  openRegisterCongrats(route?: string) {
    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpRegisterComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 1;

      if (route) {
        this.router.navigateByUrl(route);
      }
    });
  }

  async openReportsUser(imei: number, route?: string, timeZone?: string) {
    const { PopUpReportsUserComponent } = await import('../components/pop-up-reports-user/pop-up-reports-user.component');

    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpReportsUserComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.imei = imei;
      modalRef.componentInstance.timeZone = timeZone;

      if (route) {
        this.router.navigateByUrl(route);
      }
    });
  }

  async openAddMedicine(imei: number, idMedicine?: number): Promise<any> {
    const { PopUpAddMedicineComponent } = await import('../components/pop-up-add-medicine/pop-up-add-medicine.component');

    return new Promise((resolve) => {
      setTimeout(() => {
        const modalRef = this.modalService.open(PopUpAddMedicineComponent, { centered: true, backdrop: 'static', keyboard: false });
        modalRef.componentInstance.modalType = 1;
        modalRef.componentInstance.imei = imei;
        modalRef.componentInstance.idMedicine = idMedicine;
        modalRef.result.then(resolve).catch(() => resolve(null));
      });
    });
  }

  async openAddMedicineV(imei: number, idMedicine?: number): Promise<any> {
    const { PopUpAddMedicineVComponent } = await import('../components/pop-up-add-medicine-v/pop-up-add-medicine-v.component');

    return new Promise((resolve) => {
      setTimeout(() => {
        const modalRef = this.modalService.open(PopUpAddMedicineVComponent, { centered: true, backdrop: 'static', keyboard: false });
        modalRef.componentInstance.modalType = 1;
        modalRef.componentInstance.imei = imei;
        modalRef.componentInstance.idMedicine = idMedicine;
        modalRef.result.then(resolve).catch(() => resolve(null));
      });
    });
  }

  async openAcceptDocument(imei: number, route?: string) {
    const { PopUpDocumentComponent } = await import('../components/pop-up-document/pop-up-document.component');

    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpDocumentComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.imei = imei;

      if (route) {
        this.router.navigateByUrl(route);
      }
    });
  }

  async openEditHours(id: number, route?: string) {
    const { PopUpHoursComponent } = await import('../components/pop-up-hours/pop-up-hours.component');

    const modalRef = this.modalService.open(PopUpHoursComponent, { centered: true, backdrop: 'static', keyboard: false });
    modalRef.componentInstance.id = id;

    if (route) {
      this.router.navigateByUrl(route);
    }

    return modalRef;
  }


  async openChangeImei(imei?, route?: string) {
    const { PopUpChangeImeiComponent } = await import('../components/pop-up-change-imei/pop-up-change-imei.component');

    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpChangeImeiComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.imei = imei;

      if (route) {
        this.router.navigateByUrl(route);
      }
    });
  }

  async openAssignUsers(id, route?: string): Promise<any> {
    const { PopUpAssignUsersComponent } = await import('../components/pop-up-assign-users/pop-up-assign-users.component');

    return new Promise((resolve) => {
      setTimeout(() => {
        const modalRef = this.modalService.open(PopUpAssignUsersComponent, {
          centered: true,
          backdrop: 'static',
          keyboard: false
        });
        modalRef.componentInstance.modalType = 1;
        modalRef.componentInstance.id = id;

        modalRef.result.then(
          (result) => resolve(result),
          (reason) => resolve(reason)
        );

        if (route) {
          this.router.navigateByUrl(route);
        }
      });
    });
  }

  async openReportsModal(type: string, route?: string) {
    const { PopUpReportsComponent } = await import('../components/pop-up-reports/pop-up-reports.component');

    setTimeout(() => {
      const modalRef = this.modalService.open(PopUpReportsComponent, { centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.modalType = 1;
      modalRef.componentInstance.reportType = type;

      if (route) {
        this.router.navigateByUrl(route);
      }
    });
  }

  /**
   * Okno notatek. Emituje `true`, jeśli w trakcie jego otwarcia cokolwiek zapisano —
   * ekran pod spodem ma wtedy powód, żeby pobrać listę notatek na nowo. Zwykłe
   * zamknięcie oddaje `false`.
   */
  /** user-scoped (Etap 16): `userId` = notatki pacjenta po user_id (dziala tez bez opaski); bez niego stara sciezka po IMEI. */
  openAddNote(imei, route?: string, userId?: number): Observable<boolean> {
    const zamkniete: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    import('../components/pop-up-add-note/pop-up-add-note.component').then(({ PopUpAddNoteComponent }) => {
      setTimeout(() => {
        const modalRef = this.modalService.open(PopUpAddNoteComponent, { centered: true, backdrop: 'static', keyboard: false });
        modalRef.componentInstance.modalType = 1;
        modalRef.componentInstance.imei = imei;
        modalRef.componentInstance.userId = userId || null;

        modalRef.result.then(
          zapisano => { zamkniete.next(!!zapisano); zamkniete.complete(); },
          () => { zamkniete.next(false); zamkniete.complete(); }
        );

        if (route) {
          this.router.navigateByUrl(route);
        }
      });
    });

    return zamkniete.asObservable();
  }



}
